import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { signIn } from "../lib/auth.ts";
import { SocialButtons } from "../components/SocialButtons.tsx";
import { TurnstileWidget } from "../components/Turnstile.tsx";
import { PUBLIC_SIGNUP_ENABLED } from "../App.tsx";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

// Only honour `next` if it's a same-origin path. Reject anything that
// looks like an absolute URL or a protocol-relative path so attackers
// can't craft `?next=https://evil.com` and use the post-login redirect
// for phishing.
function safeNext(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/")) return "/";
  // Reject protocol-relative paths AND back-slash variants. Chrome's URL
  // parser normalises `/\evil.com` and `\/evil.com` into protocol-relative
  // forms, so the bare `startsWith("//")` check below isn't enough.
  if (raw.length >= 2 && (raw[1] === "/" || raw[1] === "\\")) return "/";
  return raw;
}

// Server-rendered paths that aren't React Router routes (Vite proxies
// them straight to the API). Navigating with React Router's `nav` would
// fall through to the "*" NotFoundPage, so for these we have to do a
// real browser navigation so the proxy gets the request.
function isServerRendered(path: string): boolean {
  return path === "/pair" || path.startsWith("/pair?") || path.startsWith("/pair/");
}

export function LoginPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Turnstile token from the (usually invisible) widget — sign-in is
  // Turnstile-guarded since 2026-08-15 (the NAS agent's password login,
  // the last headless caller, moved to service-key auth). Gated on the
  // site key so a keyless build degrades to a plain form instead of a
  // permanently disabled button; the server middleware still enforces.
  const [turnstileToken, setTurnstileToken] = useState("");
  // Pass `next` through to /signup + social buttons so the round-trip
  // survives the user picking a different auth method.
  const signupHref = next === "/" ? "/signup" : `/signup?next=${encodeURIComponent(next)}`;

  return (
    <div className="center">
      <div className="card compact">
        <h2>Sign in</h2>
        <form
          className="formstack"
          onSubmit={async (e) => {
            e.preventDefault();
            setErr(null);
            setBusy(true);
            // Token rides a header (like sign-up) so Better-Auth's body
            // schema never sees it.
            const r = await signIn.email(
              { email, password },
              { headers: { "X-Turnstile-Token": turnstileToken } },
            );
            setBusy(false);
            if (r.error) {
              setErr(r.error.message ?? "Sign-in failed");
              return;
            }
            if (isServerRendered(next)) {
              window.location.assign(next);
            } else {
              nav(next);
            }
          }}
        >
          <label>
            Email
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {TURNSTILE_SITE_KEY && (
            <TurnstileWidget
              siteKey={TURNSTILE_SITE_KEY}
              onVerify={setTurnstileToken}
              onExpired={() => setTurnstileToken("")}
              onError={() => setTurnstileToken("")}
            />
          )}
          {err && <div className="err">{err}</div>}
          <button
            type="submit"
            disabled={busy || (TURNSTILE_SITE_KEY !== "" && !turnstileToken)}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <SocialButtons callbackURL={next} />
        <p className="muted mt">
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
        {PUBLIC_SIGNUP_ENABLED && (
          <p className="muted mt">
            No account? <Link to={signupHref}>Create one</Link>
          </p>
        )}
      </div>
    </div>
  );
}
